import { useEffect, useState } from 'react';
import '../../assets/css/components/forms/forms.css';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from 'dayjs';
import { ViewIcon, ViewOffSlashIcon } from 'hugeicons-react';


export const TextInput = ({
    iconClass,
    label,
    name,
    type,
    id,
    placeholder,
    value,
    setValue,
    disabled,
    required,
    minLength,
    maxLength,
    pattern,
    errorMessage,
    onValidate
}) => {
    const [isActive, setIsActive] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [error, setError] = useState('')

    const handleIsActive = () => {
        setIsActive(!isActive)
        document.querySelectorAll('.input').forEach(input => input.classList.remove('active'));
        document.getElementById(`input${id}`).classList.add('active');
    }

    const isPasswordField = type === 'password'
    const inputType = isPasswordField ? (showPassword ? 'text' : 'password') : (type || 'text')

    const togglePasswordVisibility = (e) => {
        e.stopPropagation()
        setShowPassword(!showPassword)
    }

    const validateInput = (inputValue) => {
        let validationError = ''

        if (required && !inputValue.trim()) {
            validationError = `${label || placeholder} is required`
        } else if (type === 'email' && inputValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputValue)) {
            validationError = errorMessage || 'Please enter a valid email address (e.g., user@domain.com)'
        } else if (minLength && inputValue.length < minLength) {
            validationError = `${label || placeholder} must be at least ${minLength} characters`
        } else if (maxLength && inputValue.length > maxLength) {
            validationError = `${label || placeholder} must not exceed ${maxLength} characters`
        } else if (pattern && !new RegExp(pattern).test(inputValue)) {
            validationError = errorMessage || `${label || placeholder} format is invalid`
        }

        setError(validationError)

        if (onValidate) {
            onValidate(validationError === '')
        }

        return validationError === ''
    }

    const handleChange = (e) => {
        const newValue = e.target.value
        setValue(newValue)

        // Real-time validation for certain types
        if (type === 'email' || required || minLength || maxLength || pattern) {
            // For email, validate immediately on every change
            if (type === 'email') {
                validateInput(newValue)
            } else {
                // Debounce other validations slightly to avoid too many re-renders
                setTimeout(() => validateInput(newValue), 100)
            }
        }
    }

    const handleBlur = () => {
        // Validate on blur
        validateInput(value)
    }

    return (
        <div className="field">
            <label htmlFor={id}>
                {label}
                {required && <span className="required">*</span>}
            </label>
            <div onClick={handleIsActive} className={`input password-input ${error ? 'error' : ''}`} id={`input${id}`}>
                {iconClass}
                <input
                    value={value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    type={inputType}
                    name={name}
                    id={id}
                    placeholder={placeholder}
                    disabled={disabled}
                    required={required}
                    minLength={minLength}
                    pattern={pattern}
                />
                {isPasswordField && (
                    <span
                        className={`password-toggle ${showPassword ? 'visible' : ''}`}
                        onClick={togglePasswordVisibility}
                        title={showPassword ? 'Hide password' : 'Show password'}
                    >
                        {showPassword ? <ViewOffSlashIcon size={16} /> : <ViewIcon size={16} />}
                    </span>
                )}
            </div>
            {error && <div className="error-message">{error}</div>}
        </div>
    )
}
export const DataListInput = ({ id, name, label, placeholder, iconClass, options = [], value, setValue }) => {
    const [optionsOpen, setOptionsOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState([]);

    useEffect(() => {
        // Keep original options without modification
        setFilteredOptions(options);
    }, [options]);

    const handleInputChange = (e) => {
        const inputValue = e.target.value;
        setValue(inputValue);
        
        const filtered = options
            .filter(option => option.toLowerCase().includes(inputValue.toLowerCase()));

        setFilteredOptions(filtered);
        setOptionsOpen(true);
    };

    const handleSelectOption = (option) => {
        setValue(option);
        setOptionsOpen(false);
    };

    return (
        <div className="field">
            {label && <label htmlFor={name}>{label}</label>}
            <div className="input">
                {iconClass}
                <input
                    type="text"
                    placeholder={placeholder}
                    value={value}
                    onChange={handleInputChange}
                    onFocus={() => {
                        setFilteredOptions(options);
                        setOptionsOpen(true);
                    }}
                    onBlur={() => setTimeout(() => setOptionsOpen(false), 200)}
                />
            </div>

            {optionsOpen && (
                <div className="options">
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, index) => (
                            <p className="option" key={index} onClick={() => handleSelectOption(option)}>
                                {option}
                            </p>
                        ))
                    ) : (
                        <p className="no-data">No matching data</p> 
                    )}
                </div>
            )}
        </div>
    );
};

const DateInputMindate = dayjs('2022-01-01');
const DateInputMaxdate = dayjs('2022-01-01');
export const DateInput = ({ date, setDate, choices }) => {
    return (
        <div className="field">
            <div className="input date-input">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        value={date}
                        onChange={(newValue) => setDate(newValue)}
                        // views={choices}
                        minDate={DateInputMindate}

                    />
                </LocalizationProvider>
            </div>
        </div>
    )
}

const currentYear = dayjs();
const mindate = dayjs('2023-01-01');
export const YearInput = ({ year, setYear }) => {

    return (
        <div className="field">
            <div className="input date-input">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label={'"year"'}
                        openTo="year"
                        maxDate={currentYear}
                        minDate={mindate}
                        yearsOrder="desc"
                        value={year}
                        views={['year']}
                        onChange={(newValue) => setYear(newValue)}

                    />
                </LocalizationProvider>
            </div>
        </div>
    )
}

